import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Typography, Form, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { CardStyle } from './AuthLayout';
import { signInUrl } from '../../redux/sign-in/apiRoute';
import { notificationActiveTabEnums, userIsEmployee } from '../../utils/constant';
import { setLoginInRole, setToken, setUserInfo } from '../../redux/sign-in/userInfoSlice';
import { toast } from 'react-toastify';
import { updateNotificationTab } from '../../redux/notification/NotificationSlice';
import Bowser from 'bowser';

const { Title, Text } = Typography;
const { Password } = Input;

const getPublicIP = async () => {
  try {
    const res = await fetch('https://api.ipify.org?format=json');
    const data = await res.json();
    return data?.ip || 'Unknown IP';
  } catch (error) {
    console.error('Error fetching IP:', error);
    return 'Unknown IP';
  }
};

const getBrowserInfo = () => {
  const parsedBrowser = Bowser.parse(window.navigator.userAgent);
  return parsedBrowser.browser.name || 'Unknown Browser';
};

const getDeviceType = () => {
  const userAgent = window.navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return 'iOS';
  }
  if (/Android/i.test(userAgent)) {
    return 'Android';
  }
  if (/Windows|Mac|Linux/i.test(userAgent)) {
    return 'Desktop';
  }
  return 'Unknown';
};

const SignIn = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const onSubmit = async (values) => {
    try {
      setLoading(true);

      const ipAddress = await getPublicIP();

      const payload = {
        email: values?.email.trim(),
        password: values?.password,
        deviceType: getDeviceType(),
        userAgent: getBrowserInfo(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ipAddress: ipAddress
      };

      const response = await signInUrl(payload);

      if (response.status === 200) {
        setLoading(false);
        let loggedUserType = response?.accountData;
        let role = response?.role;
        let isEmployee = loggedUserType?.system_role === userIsEmployee;
        let lowerCaseRole = role?.role?.toLowerCase();

        if (isEmployee) {
          dispatch(updateNotificationTab(notificationActiveTabEnums.NOTIFICATIONS));
        }

        dispatch(
          setLoginInRole({
            deviceId: response?.deviceId,
            ipAddress,
            isEmployee,
            loggedUserType,
            role: { ...role, role: lowerCaseRole }
          })
        );
        dispatch(setToken(response?.access_token));
        const userDetails = response?.user_details
          ? { ...response?.user_details, isBusinessUser: response?.user_details?.department == 7 }
          : {};
        dispatch(
          setUserInfo({
            user_details: userDetails,
            permissions: response?.permissions
          })
        );
        navigate('/dashboard');

        toast.success(response?.message || 'Login Successfully');

        if (values.rememberMe) {
          localStorage.setItem('pmsEmail', values.email);
          localStorage.setItem('pmsPassword', values.password);
        } else {
          localStorage.removeItem('pmsEmail');
          localStorage.removeItem('pmsPassword');
        }
      } else {
        toast.error(response?.message || 'Unauthorized user');
      }
    } catch (error) {
      console.error('Error during sign-in:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const rememberedEmail = localStorage.getItem('pmsEmail');
    const rememberedPassword = localStorage.getItem('pmsPassword');

    if (rememberedEmail && rememberedPassword) {
      form.setFieldsValue({
        email: rememberedEmail,
        password: rememberedPassword,
        rememberMe: true
      });
    }
  }, [form]);

  return (
    <CardStyle>
      <div style={{ marginBottom: '15px' }}>
        <Title
          level={1}
          style={{
            fontSize: '48px',
            fontWeight: 700,
            margin: 0
          }}>
          Login
        </Title>
        <Text style={{ fontSize: '16px', margin: '8px 0', color: '#767676' }}>
          Please fill your details to access your account.
        </Text>
      </div>
      <Form
        form={form}
        onFinish={onSubmit}
        layout="vertical"
        initialValues={{
          rememberMe: false
        }}>
        <Form.Item
          style={{ marginBottom: '0px' }}
          label={<Text style={{ fontSize: '16px', fontWeight: 400 }}>Email</Text>}
          name="email"
          required={false}
          rules={[
            { required: true, message: 'Email is required' },
            {
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Enter a valid email address'
            }
          ]}
          validateFirst>
          <Input
            placeholder="Enter email"
            size="large"
            style={{
              height: '47px',
              borderRadius: '8px',
              fontSize: '15px'
            }}
          />
        </Form.Item>

        <br />

        <Form.Item
          style={{ marginBottom: '2px' }}
          label={<Text style={{ fontSize: '16px', fontWeight: 400 }}>Password</Text>}
          name="password"
          required={false}
          rules={[
            { required: true, message: 'Password is required' },
            { min: 8, message: 'Password must be at least 8 characters' },
            {
              pattern: /^(?=.*[0-9])(?=.*[!@#$%^&*])/,
              message: 'Password contain number, special character and alphabets'
            }
          ]}
          validateFirst>
          <Password
            placeholder="Enter password"
            size="large"
            style={{
              height: '47px',
              borderRadius: '8px',
              fontSize: '15px'
            }}
            onKeyDown={(e) => {
              if (e.key === ' ') {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const cleaned = e.target.value.replace(/\s/g, '');
              e.target.value = cleaned;
            }}
          />
        </Form.Item>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            marginBottom: '20px'
          }}>
          <Form.Item name="rememberMe" valuePropName="checked" style={{ margin: 0 }}>
            <Checkbox>Remember me</Checkbox>
          </Form.Item>

          <Text
            style={{ cursor: 'pointer', color: '#1890ff' }}
            onClick={() => navigate('/forget-password')}>
            Forgot Password?
          </Text>
        </div>

        <Form.Item style={{ margin: 0 }}>
          <Button htmlType="submit" type="primary" loading={loading} className="buttonStyle">
            Login
          </Button>
        </Form.Item>
      </Form>
    </CardStyle>
  );
};

export default SignIn;
